// An AI on the page is one more collaborator. It runs from this browser, as
// you: it sees only what you select and send, to the provider you chose,
// and it edits through the same moves as a person, as tools whose every
// call is checked. Its edits arrive as a suggestion on a copy of the page;
// accepted, they merge as its own edits, under its own name, undoable on
// their own.
// meets: ai/you-send, ai/same-moves, ai/suggests, ai/named
import { Page, HIDDEN, type TreeID } from "./doc";
import { checkProp, describeProp } from "./components";
import { PROPERTIES, allowStyle } from "./style";
import { print } from "./textform";

export type Call = { name: string; input: Record<string, unknown> };
export type Result = { ok: true; id?: TreeID } | { ok: false; reason: string };

// ---------- the moves ----------

const id = { type: "string", description: "A block's id, as in {#12@345} or id=\"12@345\"" };
export const TOOLS = [
  {
    name: "insert_component",
    description: "Insert a component from the library as a new block.",
    input_schema: {
      type: "object",
      properties: {
        parent: { ...id, description: "The container to insert into; omit for the top level of the page." },
        index: { type: "integer", minimum: 0, description: "Its place among the container's blocks, from 0." },
        type: { type: "string", description: "The component's name." },
        props: { type: "object", description: "Its settings, each checked against its schema." },
        text: { type: "string", description: "Its words, for a component that holds text." },
      },
      required: ["type", "index"],
    },
  },
  {
    name: "set_prop",
    description: "Change one setting of a block.",
    input_schema: { type: "object", properties: { node: id, key: { type: "string" }, value: {} }, required: ["node", "key", "value"] },
  },
  {
    name: "set_style",
    description: "Set one style property of a block, from the allowed list; null removes it.",
    input_schema: {
      type: "object",
      properties: { node: id, property: { type: "string", enum: Object.keys(PROPERTIES) }, value: { type: ["string", "null"] } },
      required: ["node", "property", "value"],
    },
  },
  {
    name: "move_block",
    description: "Move a block to a new place.",
    input_schema: {
      type: "object",
      properties: { node: id, parent: { ...id, description: "The container to move into; omit for the top level." }, index: { type: "integer", minimum: 0 } },
      required: ["node", "index"],
    },
  },
  {
    name: "set_text",
    description: "Replace the words of a block that holds text.",
    input_schema: { type: "object", properties: { node: id, text: { type: "string" } }, required: ["node", "text"] },
  },
] as const;

const ID = /^[0-9]+@[0-9]+$/;

// One call, checked and made, or refused with why. A refused call changes
// nothing.
export function applyTool(page: Page, call: Call, origin = "ai"): Result {
  const no = (reason: string): Result => ({ ok: false, reason });
  const input = call?.input && typeof call.input === "object" && !Array.isArray(call.input) ? call.input : {};
  const block = (v: unknown, what = "node"): TreeID | Result => {
    if (typeof v !== "string" || !ID.test(v)) return no(`“${what}” must be a block's id, like 12@345.`);
    if (!page.node(v as TreeID) || !onPage(page, v as TreeID)) return no(`There is no block ${v} on the page.`);
    return v as TreeID;
  };
  const parentOf = (v: unknown): TreeID | undefined | Result => {
    if (v === undefined || v === null) return undefined;
    const p = block(v, "parent");
    if (typeof p !== "string") return p;
    if (!page.accepts(p)) return no(`${page.type(p)} ${p} can't hold blocks.`);
    return p;
  };
  const index = (v: unknown): number | Result =>
    Number.isInteger(v) && (v as number) >= 0 && (v as number) <= 10_000 ? (v as number) : no("“index” must be a whole number from 0.");
  switch (call?.name) {
    case "insert_component": {
      const type = input.type;
      const entry = typeof type === "string" && !HIDDEN.has(type) ? page.entry(type) : undefined;
      if (!entry) return no(`There is no component called “${String(type)}”. The library has: ${Object.keys(page.library()).join(", ")}.`);
      const parent = parentOf(input.parent);
      if (typeof parent === "object") return parent;
      const at = index(input.index);
      if (typeof at === "object") return at;
      const props = input.props ?? {};
      if (typeof props !== "object" || Array.isArray(props)) return no("“props” must be an object.");
      for (const [k, v] of Object.entries(props)) {
        const p = entry.props[k];
        if (!p) return no(`${entry.label} has no setting called “${k}”.`);
        if (!checkProp(p, v)) return no(`${entry.label}'s “${k}” must be ${describeProp(p)}.`);
      }
      if (input.text !== undefined && (!entry.text || typeof input.text !== "string" || input.text.length > 5000))
        return no(entry.text ? "“text” must be text of at most 5000 characters." : `${entry.label} holds no text.`);
      const made = page.insert(parent, at, { type: entry.name, props: props as Record<string, unknown>, ...(entry.text ? { text: (input.text as string) ?? "" } : {}) }, origin);
      return { ok: true, id: made };
    }
    case "set_prop": {
      const n = block(input.node);
      if (typeof n !== "string") return n;
      if (typeof input.key !== "string") return no("“key” must be a setting's name.");
      const why = page.setProp(n, input.key, input.value, origin);
      return why ? no(why) : { ok: true };
    }
    case "set_style": {
      const n = block(input.node);
      if (typeof n !== "string") return n;
      const prop = input.property;
      if (typeof prop !== "string" || !PROPERTIES[prop]) return no(`“${String(prop)}” isn't on the allowed list of style properties.`);
      const v = input.value;
      if (v !== null && !allowStyle(prop, v)) return no(`“${String(v)}” isn't an allowed value for ${prop}.`);
      return page.setStyle(n, prop, v as string | null, origin) ? { ok: true } : no(`${prop} couldn't be set.`);
    }
    case "move_block": {
      const n = block(input.node);
      if (typeof n !== "string") return n;
      const parent = parentOf(input.parent);
      if (typeof parent === "object") return parent;
      const at = index(input.index);
      if (typeof at === "object") return at;
      return page.move(n, parent, at, origin) ? { ok: true } : no("A block can't move inside itself.");
    }
    case "set_text": {
      const n = block(input.node);
      if (typeof n !== "string") return n;
      if (!page.text(n)) return no(`${page.type(n)} ${n} holds no text.`);
      if (typeof input.text !== "string" || input.text.length > 5000) return no("“text” must be text of at most 5000 characters.");
      page.setText(n, input.text, origin);
      return { ok: true };
    }
    default:
      return no(`There is no move called “${String(call?.name)}”. The moves are: ${TOOLS.map((t) => t.name).join(", ")}.`);
  }
}

function onPage(page: Page, id: TreeID): boolean {
  for (let p: TreeID | undefined = id; p !== undefined; p = page.parent(p)) if (HIDDEN.has(page.type(p) ?? "")) return false;
  return true;
}

export function describe(page: Page, c: Call): string {
  const i = c.input ?? {};
  const name = (n: unknown) => (typeof n === "string" && page.node(n as TreeID) ? `the ${page.type(n as TreeID)}` : "a block");
  switch (c.name) {
    case "insert_component": return `Insert a ${String(i.type)}${i.text ? `: “${String(i.text).slice(0, 60)}”` : ""}`;
    case "set_prop": return `Set ${name(i.node)}'s ${String(i.key)} to ${JSON.stringify(i.value)}`;
    case "set_style": return i.value === null ? `Remove ${name(i.node)}'s ${String(i.property)}` : `Set ${name(i.node)}'s ${String(i.property)} to ${String(i.value)}`;
    case "move_block": return `Move ${name(i.node)}`;
    case "set_text": return `Change ${name(i.node)}'s words to “${String(i.text).slice(0, 80)}”`;
    default: return `${String(c.name)}`;
  }
}

// ---------- a suggestion ----------

// The AI's calls, made on a copy of the page so the shared one is
// untouched, each one a change you can take or leave.
export type Change = { call: Call; text: string; result: Result };

export class Suggestion {
  readonly copy: Page;
  readonly changes: Change[] = [];
  constructor(live: Page, calls: Call[]) {
    this.copy = new Page(live.doc.fork());
    for (const call of calls) this.changes.push({ call, text: describe(this.copy, call), result: applyTool(this.copy, call) });
  }

  // Make the chosen changes on `page` (the AI's own), in order. A change
  // that names a block another change made, and that change wasn't
  // chosen, is refused.
  accept(page: Page, chosen: number[]): Result[] {
    const made = new Map<string, TreeID>(); // id on the copy → id on the page
    const madeOnCopy = new Set(this.changes.flatMap((c) => (c.result.ok && c.result.id ? [c.result.id] : [])));
    const out: Result[] = [];
    this.changes.forEach((c, i) => {
      if (!chosen.includes(i) || !c.result.ok) return;
      const input = { ...c.call.input };
      for (const k of ["node", "parent"]) {
        const v = input[k];
        if (typeof v !== "string") continue;
        if (made.has(v)) input[k] = made.get(v);
        else if (madeOnCopy.has(v as TreeID)) return out.push({ ok: false, reason: "It's inside a block from a change that wasn't accepted." });
      }
      const r = applyTool(page, { name: c.call.name, input });
      if (r.ok && r.id && c.result.ok && c.result.id) made.set(c.result.id, r.id);
      out.push(r);
    });
    return out;
  }
}

// ---------- the AI as a collaborator ----------

// The AI is a peer of its own: a fork of this tab's doc with its own id,
// name and undo. Its edits reach the page as another peer's do.
export class Agent {
  readonly page: Page;
  constructor(private live: Page, private push: (update: Uint8Array) => void, readonly name: string) {
    this.page = new Page(live.doc.fork());
    live.name(this.page.doc.peerIdStr, name);
  }

  get peer(): string {
    return this.page.doc.peerIdStr;
  }

  private catchUp() {
    this.page.doc.import(this.live.doc.export({ mode: "update", from: this.page.doc.oplogVersion() }));
  }

  private flush() {
    const update = this.page.doc.export({ mode: "update", from: this.live.doc.oplogVersion() });
    this.live.doc.import(update);
    this.push(update);
  }

  accept(s: Suggestion, chosen: number[]): Result[] {
    this.catchUp();
    const r = s.accept(this.page, chosen);
    this.page.doc.commit();
    this.flush();
    return r;
  }

  // Undo the AI's last accepted edit, and nobody else's.
  undo(): boolean {
    this.catchUp();
    const done = this.page.undo.canUndo() && this.page.undo.undo();
    this.flush();
    return !!done;
  }
}

// ---------- asking ----------

export type Provider = "anthropic" | "openai";
export type Settings = { on: boolean; provider: Provider; model: string; key: string };
export const PROVIDERS: Record<Provider, { label: string; url: string; model: string }> = {
  anthropic: { label: "Anthropic", url: "https://api.anthropic.com/v1/messages", model: "claude-sonnet-5-5" },
  openai: { label: "OpenAI", url: "https://api.openai.com/v1/chat/completions", model: "gpt-5" },
};

export function system(page: Page): string {
  const lib = Object.values(page.library())
    .map((e) => `- ${e.name}${e.text ? " (holds text)" : ""}${e.children ? " (holds blocks)" : ""}: ${Object.entries(e.props).map(([k, p]) => `${k}: ${describeProp(p)}`).join("; ") || "no settings"}`)
    .join("\n");
  return `You edit part of a web page with the tools given, and only with them: there is no other way to change it, and no way to add code.
The part you may change is shown in its text form: markdown for words, tags for components, and each block's id in {#…} or id="…".
Components in the library:
${lib}
Style properties allowed: ${Object.keys(PROPERTIES).join(", ")}. Values may use the page's tokens, like var(--accent).
Make the fewest changes that do what is asked. Your changes are shown to a person as a suggestion they accept or not.`;
}

// What is sent: the instruction, and the selected blocks' text form. Nothing
// else about the page.
export function selectionText(page: Page, ids: TreeID[]): string {
  const all = page.toJSON();
  const picked: ReturnType<Page["toJSON"]> = [];
  const walk = (ns: typeof all) => ns.forEach((n) => (ids.includes(n.id) ? picked.push(n) : walk(n.children)));
  walk(all);
  return print(picked);
}

export async function ask(s: Settings, page: Page, instruction: string, selection: string, fetcher = fetch): Promise<Call[]> {
  if (!s.on) throw new Error("AI is off.");
  const content = `${instruction}\n\nThe selected part of the page:\n\n${selection}`;
  if (s.provider === "anthropic") {
    const res = await fetcher(PROVIDERS.anthropic.url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-api-key": s.key,
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
      },
      body: JSON.stringify({ model: s.model, max_tokens: 4096, system: system(page), tools: TOOLS, messages: [{ role: "user", content }] }),
    });
    if (!res.ok) throw new Error(`${PROVIDERS.anthropic.label} said ${res.status}: ${(await res.text()).slice(0, 200)}`);
    const body = (await res.json()) as { content?: { type: string; name?: string; input?: Record<string, unknown> }[] };
    return (body.content ?? []).filter((b) => b.type === "tool_use").map((b) => ({ name: String(b.name), input: b.input ?? {} }));
  }
  const res = await fetcher(PROVIDERS.openai.url, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${s.key}` },
    body: JSON.stringify({
      model: s.model,
      messages: [{ role: "system", content: system(page) }, { role: "user", content }],
      tools: TOOLS.map((t) => ({ type: "function", function: { name: t.name, description: t.description, parameters: t.input_schema } })),
    }),
  });
  if (!res.ok) throw new Error(`${PROVIDERS.openai.label} said ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const body = (await res.json()) as { choices?: { message?: { tool_calls?: { function: { name: string; arguments: string } }[] } }[] };
  return (body.choices?.[0]?.message?.tool_calls ?? []).map((t) => {
    let input: Record<string, unknown> = {};
    try {
      input = JSON.parse(t.function.arguments);
    } catch {}
    return { name: t.function.name, input };
  });
}

